/**
 * Context Data Service
 * Fetches budgets, categories, transactions, and lists for task context linking
 * Used by autocomplete dropdowns in AddTaskModal and EditTaskModal
 */

import { getUserBudgets } from '@/services/budgetService';
import { getAllCategories } from '@/services/categoryService';
import { getTransactions } from '@/services/transactionService2';
import { getShoppingLists } from '@/services/shoppingListService';
import { CONTEXT_TYPE } from './contextLinking';

/**
 * Fetch all budgets for autocomplete
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of budget options with id, name
 */
export async function fetchBudgetOptions(userId) {
  try {
    const budgets = await getUserBudgets(userId);
    
    return budgets.map(budget => ({
      id: budget.id,
      name: budget.name || `Budget ${budget.id.substring(0, 8)}`,
      type: CONTEXT_TYPE.BUDGET,
      // Additional metadata for smart filtering
      status: budget.status,
      cadence: budget.cadence,
      amount: budget.totalAmount,
      spent: budget.totalSpent || 0,
    }));
  } catch (error) {
    console.error('[CONTEXT DATA] Error fetching budgets:', error);
    return [];
  }
}

/**
 * Fetch all categories for autocomplete
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of category options with id, name
 */
export async function fetchCategoryOptions(userId) {
  try {
    const categories = await getAllCategories(userId);
    
    return categories.map(category => ({
      id: category.id || category.key, // Some use key instead of id
      name: category.name,
      type: CONTEXT_TYPE.CATEGORY,
      // Additional metadata
      categoryType: category.type, // expense, income, savings
      isDefault: category.isDefault,
      subcategories: category.subcategories || [],
    }));
  } catch (error) {
    console.error('[CONTEXT DATA] Error fetching categories:', error);
    return [];
  }
}

/**
 * Fetch recent transactions for autocomplete
 * @param {string} userId - User ID
 * @param {number} limit - Max number of transactions to fetch
 * @returns {Promise<Array>} Array of transaction options with id, name
 */
export async function fetchTransactionOptions(userId, limit = 50) {
  try {
    const transactions = await getTransactions(userId, {
      limit,
      // Get recent transactions from last 3 months
      startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
    });
    
    return transactions.map(transaction => ({
      id: transaction.id,
      // Create readable name: "Grocery Store - $45.50 (Nov 15)"
      name: `${transaction.description || 'Transaction'} - $${transaction.amount?.toFixed(2) || '0.00'} (${formatDateShort(transaction.date)})`,
      type: CONTEXT_TYPE.TRANSACTION,
      // Additional metadata
      amount: transaction.amount,
      transactionType: transaction.type, // expense/income
      categoryId: transaction.categoryId,
      categoryName: transaction.categoryName,
      date: transaction.date,
      description: transaction.description,
    }));
  } catch (error) {
    console.error('[CONTEXT DATA] Error fetching transactions:', error);
    return [];
  }
}

/**
 * Fetch shopping lists for autocomplete
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of list options with id, name
 */
export async function fetchListOptions(userId) {
  try {
    const { lists } = await getShoppingLists(userId, {
      status: 'active', // Only active lists
    });
    
    return lists.map(list => ({
      id: list.id,
      name: list.name,
      type: CONTEXT_TYPE.LIST,
      // Additional metadata
      icon: list.icon,
      color: list.color,
      itemCount: list.itemCount || 0,
      budget: list.budget,
      totalSpent: list.totalSpent || 0,
    }));
  } catch (error) {
    console.error('[CONTEXT DATA] Error fetching lists:', error);
    return [];
  }
}

/**
 * Fetch context options based on type
 * @param {string} userId - User ID
 * @param {string} contextType - Type of context (budget, category, transaction, list)
 * @returns {Promise<Array>} Array of options
 */
export async function fetchContextOptions(userId, contextType) {
  switch (contextType) {
    case CONTEXT_TYPE.BUDGET:
      return fetchBudgetOptions(userId);
    
    case CONTEXT_TYPE.CATEGORY:
      return fetchCategoryOptions(userId);
    
    case CONTEXT_TYPE.TRANSACTION:
      return fetchTransactionOptions(userId);
    
    case CONTEXT_TYPE.LIST:
      return fetchListOptions(userId);
    
    case CONTEXT_TYPE.GOAL:
      // Goals not implemented yet, return empty
      console.warn('[CONTEXT DATA] Goals not yet implemented');
      return [];
    
    default:
      return [];
  }
}

/**
 * Search/filter context options
 * @param {Array} options - Array of context options
 * @param {string} searchTerm - Search query
 * @returns {Array} Filtered options
 */
export function filterContextOptions(options, searchTerm) {
  if (!searchTerm || searchTerm.trim() === '') {
    return options;
  }
  
  const term = searchTerm.toLowerCase().trim();
  
  return options.filter(option => {
    // Search in name
    if (option.name.toLowerCase().includes(term)) {
      return true;
    }
    
    // For transactions, also search description
    if (option.description && option.description.toLowerCase().includes(term)) {
      return true;
    }
    
    // For categories, search in type
    if (option.categoryType && option.categoryType.toLowerCase().includes(term)) {
      return true;
    }
    
    return false;
  });
}

/**
 * Get smart suggestions based on task data
 * @param {string} userId - User ID
 * @param {Object} taskData - Task form data (category, subcategory, etc.)
 * @param {string} contextType - Selected context type
 * @returns {Promise<Array>} Suggested context options
 */
export async function getSmartSuggestions(userId, taskData, contextType) {
  try {
    const allOptions = await fetchContextOptions(userId, contextType);
    
    // No task data to base suggestions on
    if (!taskData || !taskData.category) {
      return allOptions.slice(0, 5); // Return first 5
    }
    
    const category = taskData.category?.toLowerCase();
    const subcategory = taskData.subcategory?.toLowerCase();
    
    // Budget suggestions based on category
    if (contextType === CONTEXT_TYPE.BUDGET) {
      const suggestions = allOptions.filter(budget => {
        const budgetName = budget.name.toLowerCase();
        
        // Match category keywords
        if (category?.includes('groceries') && budgetName.includes('food')) return true;
        if (category?.includes('bills') && (budgetName.includes('utilities') || budgetName.includes('bills'))) return true;
        if (category?.includes('transport') && budgetName.includes('transport')) return true;
        if (category?.includes('entertainment') && budgetName.includes('entertainment')) return true;
        if (category?.includes('housing') && (budgetName.includes('rent') || budgetName.includes('housing'))) return true;
        
        return false;
      });
      
      return suggestions.length > 0 ? suggestions : allOptions.slice(0, 5);
    }
    
    // Category suggestions based on task category
    if (contextType === CONTEXT_TYPE.CATEGORY) {
      const suggestions = allOptions.filter(cat => {
        const catName = cat.name.toLowerCase();
        
        // Direct match
        if (catName.includes(category) || catName.includes(subcategory)) {
          return true;
        }
        
        return false;
      });
      
      return suggestions.length > 0 ? suggestions : allOptions.slice(0, 5);
    }
    
    // List suggestions for groceries
    if (contextType === CONTEXT_TYPE.LIST && category?.includes('groceries')) {
      return allOptions.slice(0, 5);
    }
    
    // Transaction suggestions - most recent
    if (contextType === CONTEXT_TYPE.TRANSACTION) {
      return allOptions.slice(0, 10);
    }
    
    return allOptions.slice(0, 5);
  } catch (error) {
    console.error('[CONTEXT DATA] Error getting smart suggestions:', error);
    return [];
  }
}

/**
 * Validate that a linked context object still exists
 * @param {string} userId - User ID
 * @param {string} contextType - Type of context
 * @param {string} contextId - ID of linked object
 * @returns {Promise<boolean>} True if object exists
 */
export async function validateContextLink(userId, contextType, contextId) {
  if (!contextId || !contextType) return false;
  
  try {
    const options = await fetchContextOptions(userId, contextType);
    return options.some(option => option.id === contextId);
  } catch (error) {
    console.error('[CONTEXT DATA] Error validating context link:', error);
    return false;
  }
}

/**
 * Helper: Format date for display
 */
function formatDateShort(date) {
  if (!date) return '';
  
  const d = date instanceof Date ? date : new Date(date);
  const month = d.toLocaleString('default', { month: 'short' });
  const day = d.getDate();
  
  return `${month} ${day}`;
}
